import React, { useState, useId } from 'react';
import {
  CharacterCount,
  HelperText,
  Label,
  StyledTextarea,
  TextareaContainer,
} from './textArea.style';

// Main component
const Textarea = ({
  label,
  id,
  name,
  value,
  onChange,
  placeholder = '',
  required = false,
  disabled = false,
  rows = 4,
  maxLength,
  helperText = '',
  errorMessage = '',
  showCharacterCount = false,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const uniqueId = useId();
  const textareaId = id || `textarea-${uniqueId}`;
  const helperTextId = `${textareaId}-helper`;
  const errorId = `${textareaId}-error`;
  const charCountId = `${textareaId}-count`;

  const currentValue = value !== undefined ? value : internalValue;
  const hasError = Boolean(errorMessage);
  const isNearLimit = maxLength && currentValue.length > maxLength * 0.8;
  const isOverLimit = maxLength && currentValue.length > maxLength;

  const handleChange = (e) => {
    const newValue = e.target.value;

    if (maxLength && newValue.length > maxLength) {
      return; // Prevent exceeding max length
    }

    if (value === undefined) {
      setInternalValue(newValue);
    }

    if (onChange) {
      onChange(e);
    }
  };

  // Build aria-describedby attribute
  const describedByIds = [];
  if (helperText && !hasError) describedByIds.push(helperTextId);
  if (hasError) describedByIds.push(errorId);
  if (showCharacterCount && maxLength) describedByIds.push(charCountId);
  if (ariaDescribedBy) describedByIds.push(ariaDescribedBy);

  const ariaDescribedByValue = describedByIds.length > 0 ? describedByIds.join(' ') : undefined;

  return (
    <TextareaContainer>
      {label && (
        <Label htmlFor={textareaId} className={required ? 'required' : ''}>
          {label}
        </Label>
      )}

      <StyledTextarea
        id={textareaId}
        name={name}
        value={currentValue}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        aria-invalid={hasError}
        aria-describedby={ariaDescribedByValue}
        aria-required={required}
        {...props}
      />

      {helperText && !hasError && <HelperText id={helperTextId}>{helperText}</HelperText>}

      {hasError && (
        <HelperText id={errorId} className="error" role="alert">
          {errorMessage}
        </HelperText>
      )}

      {showCharacterCount && maxLength && (
        <CharacterCount
          id={charCountId}
          className={isOverLimit ? 'error' : isNearLimit ? 'warning' : ''}
          aria-live="polite"
        >
          {currentValue.length}/{maxLength}
        </CharacterCount>
      )}
    </TextareaContainer>
  );
};

export default Textarea;
